import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { formatCurrency } from '@sortlist-frontend/utils';

import { rankingConfig } from '../../ranking.config';
import type { RankingFullRankingEntry, RankingFullRankingFilters } from '../../types';
import { FULL_RANKING_LIST_ID, FULL_RANKING_SCRIPT } from './fullRankingScript';
import { FullRankingTable } from './FullRankingTable';

interface FilterableFullRankingProps {
  caption: string;
  entries: readonly RankingFullRankingEntry[];
  filters: RankingFullRankingFilters;
  locale: string;
}

const LABEL_CLASS = 'flex flex-col gap-1 text-xs font-medium text-secondary-500';
const CONTROL_CLASS = 'min-w-0 rounded-md border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-950';

const countriesByFrequency = (entries: readonly RankingFullRankingEntry[]) => {
  const counts = new Map<string, number>();
  entries.forEach((entry) => {
    if (entry.country == null) return;
    counts.set(entry.country, (counts.get(entry.country) ?? 0) + 1);
  });
  return [...counts.entries()]
    .sort(([nameA, countA], [nameB, countB]) => (countA === countB ? nameA.localeCompare(nameB) : countB - countA))
    .map(([name]) => name);
};

export const FilterableFullRanking = (props: FilterableFullRankingProps) => {
  const { caption, entries, filters, locale } = props;
  const { t } = useTranslation(rankingConfig.i18nNamespaces);
  const countries = countriesByFrequency(entries);
  const shownCount = Math.min(filters.visibleCount, entries.length);

  return (
    <div id={FULL_RANKING_LIST_ID} data-visible={filters.visibleCount}>
      <div
        data-f="filters"
        hidden
        className="mb-4 grid gap-3 rounded-2xl border border-secondary-200 bg-white p-4 shadow-xs md:grid-cols-4">
        <label className={LABEL_CLASS}>
          {t('longtail:ranking.fullRanking.filters.search')}
          <input type="search" data-f="q" className={CONTROL_CLASS} />
        </label>
        <label className={LABEL_CLASS}>
          {t('longtail:ranking.fullRanking.filters.country')}
          <select data-f="country" className={CONTROL_CLASS}>
            <option value="">{t('longtail:ranking.fullRanking.filters.allCountries')}</option>
            {countries.map((country) => (
              <option key={country}>{country}</option>
            ))}
          </select>
        </label>
        <label className={LABEL_CLASS}>
          {t('longtail:ranking.fullRanking.filters.budget')}
          <select data-f="budget" className={CONTROL_CLASS}>
            <option value="">{t('longtail:ranking.fullRanking.filters.anyBudget')}</option>
            {filters.budgetThresholds.map((threshold) => (
              <option key={threshold} value={threshold}>
                {t('longtail:ranking.fullRanking.filters.budgetUpTo', {
                  amount: formatCurrency({ value: threshold, locale, rounded: true }),
                })}
              </option>
            ))}
          </select>
        </label>
        <label className={LABEL_CLASS}>
          {t('longtail:ranking.fullRanking.filters.sort')}
          <select data-f="sort" className={CONTROL_CLASS}>
            <option value="score">{t('longtail:ranking.fullRanking.filters.sortScore')}</option>
            <option value="rating">{t('longtail:ranking.fullRanking.filters.sortRating')}</option>
            <option value="reviews">{t('longtail:ranking.fullRanking.filters.sortReviews')}</option>
            <option value="budget">{t('longtail:ranking.fullRanking.filters.sortBudget')}</option>
          </select>
        </label>
      </div>

      <FullRankingTable caption={caption} entries={entries} locale={locale} isFilterable />

      <div data-f="footer" hidden className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <button
          type="button"
          data-f="more"
          data-more={t('longtail:ranking.fullRanking.filters.showAll', { count: entries.length })}
          data-less={t('longtail:ranking.fullRanking.filters.showFewer')}
          className="rounded-full border border-primary-500 px-4 py-2 font-semibold text-primary-500 transition-colors hover:bg-primary-500 hover:text-white">
          {t('longtail:ranking.fullRanking.filters.showAll', { count: entries.length })}
        </button>
        <span
          data-f="count"
          data-label={t('longtail:ranking.fullRanking.filters.shownCount', { shown: '{n}', total: '{t}' })}
          aria-live="polite"
          className="text-secondary-500">
          {t('longtail:ranking.fullRanking.filters.shownCount', { shown: shownCount, total: entries.length })}
        </span>
      </div>

      <script dangerouslySetInnerHTML={{ __html: FULL_RANKING_SCRIPT }} />
    </div>
  );
};
